// Timeline pagination utilities for A4 printing
import { TimelineItem } from '@/types/item';
import { formatDateForBackend } from '@/utils/dateFormatUtils';

export interface TimelinePage {
  startDate: string;
  endDate: string;
  items: TimelineItem[];
  pageNumber: number;
  totalPages: number;
  dayWidth: number;
}

export interface TimelinePaginationConfig {
  pageFormat?: 'portrait' | 'landscape';
  maxDaysPerPage?: number;
  dayWidth?: number;
  overlapDays?: number;
  customTimelineRange?: { startDate: string; endDate: string };
  stretchToFullWidth?: boolean;
  includeCategory?: boolean;
  includeProject?: boolean;
  paginationMode?: 'by-items' | 'by-date-range';
  maxItemsPerPage?: number;
  fitWidthMaxDay?: number;
  extraLeftWidth?: number;
}

export const calculateTimelineDensity = (
  totalDays: number, 
  pageFormat: 'portrait' | 'landscape' = 'landscape',
  stretchToFullWidth: boolean = false,
  includeCategory: boolean = true,
  includeProject: boolean = false,
  paginationMode: 'by-items' | 'by-date-range' = 'by-items',
  fitWidthMaxDay?: number,
  extraLeftWidth?: number
) => {
  // A4 width constraints: optimized for full width utilization
  const usableWidth = pageFormat === 'portrait' ? 700 : 1000;
  
  // Calculate left fixed columns width dynamically
  // Base: Item (180) + % (35) + Dur (35) + Start (55) + End (55) = 360
  const baseWidth = 180 + 35 + 35 + 55 + 55;
  const leftFixedWidth = baseWidth + (includeCategory ? 80 : 0) + (includeProject ? 100 : 0) + (extraLeftWidth ?? 0);
  const timelineWidth = usableWidth - leftFixedWidth;
  
  // Width bounds for readability and visual quality
  const minDayWidth = paginationMode === 'by-items' ? 1 : 4; // by-items: allow smaller width to fit full timeline
  const maxDayWidth = stretchToFullWidth ? 50 : 15; // Stretch mode: allow wider columns to fill paper
  
  // Calculate optimal day width to fill available space
  let optimalDayWidth = timelineWidth / totalDays;
  
  // Apply bounds (allow caller override via fitWidthMaxDay)
  const cap = fitWidthMaxDay ?? maxDayWidth;
  if (optimalDayWidth > cap) {
    // Timeline is short - cap at max width for better aesthetics
    optimalDayWidth = cap;
  }
  
  let dayWidth: number;
  let maxDaysPerPage: number;
  let pagesNeeded: number;
  
  if (paginationMode === 'by-items') {
    // BY-ITEMS: Always fit full timeline on page, even if dayWidth < 4px
    dayWidth = Math.max(minDayWidth, Math.floor(optimalDayWidth * 10) / 10);
    maxDaysPerPage = totalDays; // All days visible
    pagesNeeded = 1;
  } else if (optimalDayWidth >= 4) {
    // Single page: Timeline fits comfortably with optimal width
    dayWidth = Math.floor(optimalDayWidth * 10) / 10; // Round to 1 decimal
    maxDaysPerPage = Math.ceil(timelineWidth / dayWidth);
    pagesNeeded = 1;
  } else {
    // Multiple pages: Timeline too long, use minimum readable width
    dayWidth = 4;
    maxDaysPerPage = Math.floor(timelineWidth / dayWidth);
    pagesNeeded = Math.ceil(totalDays / maxDaysPerPage);
  }
  
  return {
    dayWidth,
    maxDaysPerPage,
    pagesNeeded,
    pageFormat,
    overlapDays: 3,
    recommendedFormat: totalDays <= 30 ? 'portrait' : 'landscape'
  };
};

export const getTimelineRange = (items: TimelineItem[]) => {
  if (items.length === 0) {
    const today = formatDateForBackend(new Date())!;
    return { startDate: today, endDate: today, totalDays: 1 };
  }
  
  const allDates: Date[] = [];
  
  items.forEach(item => {
    // Add item start and end dates
    allDates.push(new Date(item.startDate));
    allDates.push(new Date(item.endDate));
    
    // Add dates from timeline data gates
    if (item.timelineData && typeof item.timelineData === 'object') {
      Object.values(item.timelineData).forEach(functionData => {
        if (functionData && typeof functionData === 'object') {
          ['g1', 'g2', 'g3', 'g4', 'g5'].forEach(gate => {
            const gateData = (functionData as any)[gate];
            if (gateData && typeof gateData === 'object') {
              if (gateData.start) allDates.push(new Date(gateData.start));
              if (gateData.end) allDates.push(new Date(gateData.end));
            }
          });
        }
      });
    }
  });
  
  const startDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const endDate = new Date(Math.max(...allDates.map(d => d.getTime())));
  
  const totalDaysBeforePadding = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // Add minimal padding (only for short timelines)
  const paddingDays = totalDaysBeforePadding < 30 ? 3 : 0;
  startDate.setDate(startDate.getDate() - paddingDays);
  endDate.setDate(endDate.getDate() + paddingDays);
  
  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  return {
    startDate: formatDateForBackend(startDate)!,
    endDate: formatDateForBackend(endDate)!,
    totalDays
  };
};

export const paginateTimeline = (items: TimelineItem[], config?: Partial<TimelinePaginationConfig>): TimelinePage[] => {
  // Determine timeline range
  let startDate: string, endDate: string, totalDays: number;
  
  if (config?.customTimelineRange) {
    // Use custom range if provided
    startDate = config.customTimelineRange.startDate;
    endDate = config.customTimelineRange.endDate;
    const start = new Date(startDate);
    const end = new Date(endDate);
    totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  } else {
    // Auto-calculate from items
    const range = getTimelineRange(items);
    startDate = range.startDate;
    endDate = range.endDate;
    totalDays = range.totalDays;
  }
  
  const density = calculateTimelineDensity(
    totalDays, 
    config?.pageFormat, 
    config?.stretchToFullWidth,
    config?.includeCategory ?? true,
    config?.includeProject ?? false,
    config?.paginationMode || 'by-items',
    config?.fitWidthMaxDay,
    config?.extraLeftWidth
  );
  
  const finalConfig: TimelinePaginationConfig = {
    pageFormat: 'landscape',
    maxDaysPerPage: density.maxDaysPerPage,
    dayWidth: density.dayWidth,
    overlapDays: density.overlapDays,
    paginationMode: 'by-items',
    // Disable item pagination here - let chunkItemsForPrint() in export handle it
    // This returns ALL items in a single page, export will chunk for printing
    maxItemsPerPage: 9999,
    ...config
  };
  
  // BY-ITEMS PAGINATION: Each item appears once with full timeline
  if (finalConfig.paginationMode === 'by-items') {
    const maxItemsPerPage = finalConfig.maxItemsPerPage || 20;
    const totalPages = Math.ceil(items.length / maxItemsPerPage);
    const pages: TimelinePage[] = [];
    
    for (let i = 0; i < items.length; i += maxItemsPerPage) {
      const pageItems = items.slice(i, i + maxItemsPerPage);
      pages.push({
        startDate, // Global timeline range
        endDate,   // Global timeline range
        items: pageItems,
        pageNumber: pages.length + 1,
        totalPages,
        dayWidth: finalConfig.dayWidth
      });
    }
    
    return pages;
  }
  
  // BY-DATE-RANGE PAGINATION (legacy mode)
  // If density calculation says single page, force single page
  if (density.pagesNeeded === 1) {
    return [{
      startDate,
      endDate,
      items,
      pageNumber: 1,
      totalPages: 1,
      dayWidth: finalConfig.dayWidth
    }];
  }
  
  // Multi-page pagination by date range (legacy mode)
  const pages: TimelinePage[] = [];
  const start = new Date(startDate);
  
  let pageNumber = 1;
  
  while (start < new Date(endDate)) {
    const pageStartDate = new Date(start);
    const pageEndDate = new Date(start);
    pageEndDate.setDate(pageEndDate.getDate() + finalConfig.maxDaysPerPage - 1);
    
    // Don't exceed the overall end date
    if (pageEndDate > new Date(endDate)) {
      pageEndDate.setTime(new Date(endDate).getTime());
    }
    
    // Filter items that overlap with this page
    const pageItems = items.filter(item => {
      const itemStart = new Date(item.startDate);
      const itemEnd = new Date(item.endDate);
      return itemEnd >= pageStartDate && itemStart <= pageEndDate;
    });
    
    pages.push({
      startDate: formatDateForBackend(pageStartDate)!,
      endDate: formatDateForBackend(pageEndDate)!,
      items: pageItems,
      pageNumber,
      totalPages: density.pagesNeeded,
      dayWidth: finalConfig.dayWidth
    });
    
    // Move to next page (with overlap)
    start.setDate(start.getDate() + finalConfig.maxDaysPerPage - finalConfig.overlapDays);
    pageNumber++;
    
    // Safety break
    if (pageNumber > 20) break;
  }
  
  // Update total pages for all pages
  pages.forEach(page => {
    page.totalPages = pages.length;
  });
  
  return pages;
};

export const generateDateColumns = (startDate: string, endDate: string, dayWidth: number) => {
  const columns = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  while (start <= end) {
    columns.push({
      date: formatDateForBackend(start)!,
      dayOfWeek: start.getDay(),
      isWeekend: start.getDay() === 0 || start.getDay() === 6,
      isMonthStart: start.getDate() === 1,
      width: dayWidth
    });
    start.setDate(start.getDate() + 1);
  }
  
  return columns;
};